export const SHOW_HARDKEY_DEBUG = false

export type HardKeyHandler = (event: KeyboardEvent) => void | Promise<void>

export enum KeyenceHardKey {
  F1 = 'F1',
  F2 = 'F2',
  F3 = 'F3',
  F4 = 'F4',
  BACKSPACE = 'Backspace', // C-KEY in Keyence HT
  ENTER = 'Enter', // ENT-KEY in Keyence HT

  ARROW_UP = 'ArrowUp',
  ARROW_DOWN = 'ArrowDown',
  ARROW_LEFT = 'ArrowLeft',
  ARROW_RIGHT = 'ArrowRight',
  NUM_1 = 'Digit1',
  NUM_2 = 'Digit2',
  NUM_3 = 'Digit3',
  NUM_4 = 'Digit4',
  NUM_5 = 'Digit5',
  NUM_6 = 'Digit6',
  NUM_7 = 'Digit7',
  NUM_8 = 'Digit8',
  NUM_9 = 'Digit9',
  NUM_0 = 'Digit0',
  PERIOD = 'Period',
  MINUS = 'Minus',

  TAB = 'Tab',
}

interface HardKeyRegistration {
  componentId: symbol
  key: KeyenceHardKey
  handler: HardKeyHandler
  preventDefault: boolean
  stopPropagation: boolean
  enabled: boolean
  timestamp: number
}

interface RegisterOptions {
  preventDefault?: boolean
  stopPropagation?: boolean
}

interface Layer {
  id: symbol
  ownerComponentId: symbol | null
  handlers: HardKeyRegistration[]
}

class KeyenceHardKeyEventManager {
  private layerStack: Layer[] = []
  private boundListener: ((event: KeyboardEvent) => void) | null = null

  constructor() {
    this.layerStack.push({
      id: Symbol('base-layer'),
      ownerComponentId: null,
      handlers: [],
    })
    this.setupPageLifecycleGuards()
  }

  private setupPageLifecycleGuards(): void {
    const resetHardKeyState = () => {
      this.resetToBaseLayer()
    }

    window.addEventListener('beforeunload', resetHardKeyState)
    window.addEventListener('pagehide', resetHardKeyState)
    window.addEventListener('pageshow', (event: PageTransitionEvent) => {
      if (event.persisted) {
        resetHardKeyState()
      }
    })
  }

  private resetToBaseLayer(): void {
    this.layerStack = [this.layerStack[0]]
    this.layerStack[0].handlers = []
    if (this.boundListener) {
      window.removeEventListener('keydown', this.boundListener)
      this.boundListener = null
    }
  }

  private ensureListener(): void {
    if (!this.boundListener) {
      this.boundListener = this.handleKeyDown.bind(this)
      window.addEventListener('keydown', this.boundListener)
    }
  }

  private cleanupListenerIfEmpty(): void {
    const hasAnyHandlers = this.layerStack.some((layer) => layer.handlers.length > 0)
    if (!hasAnyHandlers && this.boundListener) {
      window.removeEventListener('keydown', this.boundListener)
      this.boundListener = null
    }
  }

  private handleKeyDown(event: KeyboardEvent): void {
    if (this.layerStack.length === 0) {
      return
    }

    const currentLayer = this.getCurrentLayer()

    const matchingHandlers = currentLayer.handlers.filter(
      (h) => h.key === event.code && h.enabled
    )

    if (matchingHandlers.length > 0) {
      const latestHandler = matchingHandlers.reduce((latest, current) =>
        current.timestamp > latest.timestamp ? current : latest
      )

      if (latestHandler.preventDefault) {
        event.preventDefault()
      }
      if (latestHandler.stopPropagation) {
        event.stopPropagation()
      }
      latestHandler.handler(event)
    }
  }

  getCurrentLayer(): Layer {
    return this.layerStack[this.layerStack.length - 1]
  }

  register(componentId: symbol, key: KeyenceHardKey, handler: HardKeyHandler, options: RegisterOptions = {}): void {
    this.ensureListener()

    const currentLayer = this.getCurrentLayer()

    currentLayer.handlers.push({
      componentId,
      key,
      handler,
      preventDefault: options.preventDefault ?? false,
      stopPropagation: options.stopPropagation ?? false,
      enabled: true,
      timestamp: Date.now(),
    })
  }

  registerMultiple(
    componentId: symbol,
    keys: KeyenceHardKey[],
    handler: HardKeyHandler,
    options: RegisterOptions = {}
  ): void {
    keys.forEach((key) => {
      this.register(componentId, key, handler, options)
    })
  }

  pushLayer(ownerComponentId: symbol | null = null, handlers?: HardKeyRegistration[]): symbol {
    const newLayerId = Symbol('hard-key-layer')
    this.layerStack.push({
      id: newLayerId,
      ownerComponentId,
      handlers: handlers || [],
    })
    return newLayerId
  }

  popLayer(layerId?: symbol): void {
    if (this.layerStack.length <= 1) {
      this.cleanupListenerIfEmpty()
      return
    }

    if (!layerId) {
      this.layerStack.pop()
      this.cleanupListenerIfEmpty()
      return
    }

    const targetIndex = this.layerStack.findIndex((layer, index) => index > 0 && layer.id === layerId)
    if (targetIndex !== -1) {
      this.layerStack.splice(targetIndex, 1)
    }
    this.cleanupListenerIfEmpty()
  }

  unregister(componentId: symbol, key: KeyenceHardKey): void {
    for (const layer of this.layerStack) {
      const index = layer.handlers.findIndex((h) => h.componentId === componentId && h.key === key)
      if (index !== -1) {
        layer.handlers.splice(index, 1)
        break
      }
    }
    this.cleanupListenerIfEmpty()
  }

  unregisterAll(componentId: symbol): void {
    for (const layer of this.layerStack) {
      layer.handlers = layer.handlers.filter((h) => h.componentId !== componentId)
    }
    this.cleanupListenerIfEmpty()
  }

  setEnabled(componentId: symbol, enabled: boolean): void {
    for (const layer of this.layerStack) {
      layer.handlers.forEach((handler) => {
        if (handler.componentId === componentId) {
          handler.enabled = enabled
        }
      })
    }
  }

  hasRegistrations(componentId: symbol): boolean {
    return this.layerStack.some((layer) => layer.handlers.some((h) => h.componentId === componentId))
  }

  getTotalRegistrationCount(): number {
    return this.layerStack.reduce((count, layer) => count + layer.handlers.length, 0)
  }

  getLayerDepth(): number {
    return this.layerStack.length
  }

  getActiveHandlers(): HardKeyRegistration[] {
    if (this.layerStack.length === 0) return []
    return this.layerStack[this.layerStack.length - 1].handlers
  }

  getAllLayers(): Layer[] {
    return this.layerStack
  }
}

export const keyenceHardKeyEventManager = new KeyenceHardKeyEventManager()

export function useKeyenceHardKeyEvents(options: { newLayer?: boolean } = {}) {
  const componentId = Symbol('keyence-hard-key-component')

  let layerPushed = false
  let pushedLayerId: symbol | null = null

  const registerHardKey = (key: KeyenceHardKey, handler: HardKeyHandler, registerOptions?: RegisterOptions): void => {
    if (options.newLayer && !layerPushed) {
      pushedLayerId = keyenceHardKeyEventManager.pushLayer(componentId)
      layerPushed = true
    }
    keyenceHardKeyEventManager.register(componentId, key, handler, registerOptions)
  }

  const registerMultipleHardKeys = (
    keys: KeyenceHardKey[],
    handler: HardKeyHandler,
    registerOptions?: RegisterOptions
  ): void => {
    if (options.newLayer && !layerPushed) {
      pushedLayerId = keyenceHardKeyEventManager.pushLayer(componentId)
      layerPushed = true
    }
    keyenceHardKeyEventManager.registerMultiple(componentId, keys, handler, registerOptions)
  }

  const unregisterHardKey = (key: KeyenceHardKey): void => {
    keyenceHardKeyEventManager.unregister(componentId, key)
  }

  const unregisterAllHardKeys = (): void => {
    keyenceHardKeyEventManager.unregisterAll(componentId)
    if (layerPushed) {
      keyenceHardKeyEventManager.popLayer(pushedLayerId ?? undefined)
      pushedLayerId = null
      layerPushed = false
    }
  }

  const hasRegisteredKeys = (): boolean => {
    return keyenceHardKeyEventManager.hasRegistrations(componentId)
  }

  const setEnabled = (enabled: boolean): void => {
    keyenceHardKeyEventManager.setEnabled(componentId, enabled)
  }

  return {
    registerHardKey,
    registerMultipleHardKeys,
    unregisterHardKey,
    unregisterAllHardKeys,
    hasRegisteredKeys,
    setEnabled,
    KeyenceHardKey,
  }
}

export default keyenceHardKeyEventManager
